import { ExternalLink, MessageCircleHeart } from 'lucide-react';
import { KOROKORO_PROMO } from '../config/promoBanner';

/**
 * 運営者が別途運営している Misskey サーバー「ころがすきー」への紹介バナー。
 *
 * otomochi 自身の CTA と見分けがつくよう「otomochi の運営者より」で出所を明示する。
 *
 * 表示は画面幅で切り替える。配布されたバナー画像は 1456x180（約8:1）と横長で、
 * SP 幅まで縮めると焼き込まれた文字が潰れて読めないため、狭い画面ではテキスト版に
 * 差し替える。画像版でも小さすぎる年齢条件だけは、下に本文テキストとして併記する。
 */
export default function PromoBanner() {
  const promo = KOROKORO_PROMO;

  return (
    <aside aria-label={`${promo.name}のご紹介`} className="max-w-4xl mx-auto px-4 pb-12 pt-4">
      <p className="text-xs font-medium text-text-sub mb-2">otomochi の運営者より</p>

      {promo.imageSrc ? (
        <>
          {/* SP: 画像は文字が潰れるためテキスト版で見せる */}
          <div className="sm:hidden">
            <TextBanner />
          </div>

          {/* sm 以上: 配布されたバナー画像をそのまま見せる */}
          <a
            href={promo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block rounded-panel overflow-hidden transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <img
              src={promo.imageSrc}
              alt={promo.imageAlt}
              width={promo.imageWidth}
              height={promo.imageHeight}
              loading="lazy"
              className="w-full h-auto"
            />
          </a>
          {/* 画像内にも年齢条件はあるが、縮小すると読めない大きさになるため本文でも示す */}
          <p className="hidden sm:block text-xs text-text-sub mt-2">{promo.ageNote}</p>
        </>
      ) : (
        <TextBanner />
      )}
    </aside>
  );
}

/** 画像を使わない版。バナー画像が未設定のときと、SP 幅で使う。 */
function TextBanner() {
  const promo = KOROKORO_PROMO;

  return (
    <div className="rounded-panel border border-emerald-600/20 bg-emerald-50/70 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
          <MessageCircleHeart className="w-8 h-8 text-white" aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-text-heading">
            {promo.name}
            <span className="ml-2 text-sm font-medium text-text-sub">{promo.tagline}</span>
          </h2>
          <p className="text-sm text-text-sub leading-relaxed mt-1.5">
            otomochi を作っているヘイリックが、非営利・無料で運営している Misskey
            サーバーです。TRPG好きな人とその周りの人たちが、日常や趣味、創作の話をゆるくしています。
            同卓仲間を探す場所としても、ただ雑談する場所としても使ってください。
          </p>
          <p className="text-xs text-text-sub mt-2">{promo.ageNote}</p>
        </div>

        <a
          href={promo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 flex-shrink-0 rounded-control bg-emerald-600 px-6 py-2.5 text-base font-medium text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
        >
          <span>のぞいてみる</span>
          <ExternalLink className="w-4 h-4" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
